import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { buildExcelDocument } from '@/lib/excelExport';
import { useAuth } from '@/hooks/useAuth';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { getCommerceId } from '@/lib/ownership';
import { logAudit } from '@/lib/coreAudit';
import { grantDigitalAccess, searchZifekClients, type ZifekClient } from '@/lib/digitalAccess';
import { attributeOrderToEmployee, getOrganizationByCommerce, listOrganizationEmployees, type CoreEmployee } from '@/lib/zifekCore';
import { listCoreStatuses, statusColorClass, type CoreStatus } from '@/lib/coreSystem';
import { computeCommission } from '@/lib/coreCatalog';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { printInvoice } from '@/lib/invoiceShare';
import type { FinanceInvoiceItem } from '@/lib/finance';
import { reserveDocNumber } from '@/lib/invoiceNumbering';
import type { FinanceInvoice } from '@/lib/finance';
import EntityActivityTabs from '@/components/feature/EntityActivityTabs';
import PackOrderDetails from './components/PackOrderDetails';

interface CommandeRow {
  id: number;
  titre: string | null;
  typecommande: string | null;
  nomclient: string | null;
  email: string | null;
  tel: string | null;
  adresse: string | null;
  ville: string | null;
  details: string | null;
  noteclient: string | null;
  monaie: string | null;
  totalttc: string | null;
  quantite: number | null;
  methodepayment: string | null;
  statut_com_vendeur: string | null;
  statut_com_client: string | null;
  date_time: string | null;
  user_id: string | null;
  product_id: number | null;
  sommepayee: string | null;
  restant: string | null;
  core_employee_id: number | null;
  commission_amount: number | null;
  items_json: Array<{ id: number; name: string; quantity: number; product_type?: string }> | null;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  'En attente': { label: 'En attente', className: 'bg-amber-100 text-amber-700' },
  'Confirmée': { label: 'Confirmée', className: 'bg-primary-100 text-primary-700' },
  'Préparation': { label: 'Préparation', className: 'bg-secondary-100 text-secondary-700' },
  'Expédiée': { label: 'Expédiée', className: 'bg-secondary-100 text-secondary-700' },
  'Livrée': { label: 'Livrée', className: 'bg-accent-100 text-accent-700' },
  'Annulée': { label: 'Annulée', className: 'bg-red-100 text-red-700' },
};

const STATUS_KEYS = ['En attente', 'Confirmée', 'Préparation', 'Expédiée', 'Livrée', 'Annulée'];

function toAmount(value: string | number | null): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '0'));
  return Number.isNaN(n) ? 0 : n;
}

export default function OrdersPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<CommandeRow[]>([]);
  const [creatingExcel, setCreatingExcel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [validatingId, setValidatingId] = useState<number | null>(null);
  const [invoiceBusy, setInvoiceBusy] = useState<number | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [accessOrder, setAccessOrder] = useState<CommandeRow | null>(null);
  const [guestQuery, setGuestQuery] = useState('');
  const [guestResults, setGuestResults] = useState<ZifekClient[]>([]);
  const [guestSearching, setGuestSearching] = useState(false);
  const [granting, setGranting] = useState(false);
  const [grantMsg, setGrantMsg] = useState<string | null>(null);
  const [employees, setEmployees] = useState<CoreEmployee[]>([]);
  const [coreOrderStatuses, setCoreOrderStatuses] = useState<CoreStatus[]>([]);
  const [attrBusy, setAttrBusy] = useState<number | null>(null);
  const { equivalence } = useDisplayCurrency(user?.monaie || 'MAD');
  const company = useCompanyProfile();

  const commerceId = getCommerceId(user);

  // Journalise une action sur une commande (auteur + horodatage) — best-effort.
  const logOrderAction = useCallback(
    async (action: string, orderId: number, resourceType: string, detail: Record<string, unknown> | null) => {
      try {
        if (!commerceId) return;
        const org = await getOrganizationByCommerce(commerceId);
        if (!org) return;
        await logAudit({
          tenantId: org.tenant_id, organizationId: org.id,
          userId: user?.id ?? null, userName: user?.name ?? null,
          action, resourceType, resourceId: String(orderId), after: detail,
        });
      } catch { /* best-effort */ }
    },
    [commerceId, user],
  );

  const fetchOrders = useCallback(async () => {
    if (!commerceId) return;
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('commande')
        .select('*')
        .eq('idvendeur', commerceId)
        .order('date_time', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('statut_com_vendeur', statusFilter);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setOrders((data as CommandeRow[]) || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, commerceId]);

  useEffect(() => {
    if (user) fetchOrders();
  }, [fetchOrders, user]);

  // Ouverture ciblée d'une commande depuis une notification (/dashboard/orders?order=ID).
  useEffect(() => {
    const orderParam = searchParams.get('order');
    if (!orderParam || orders.length === 0) return;
    const id = Number(orderParam);
    if (!Number.isFinite(id) || !orders.some((o) => o.id === id)) return;
    setStatusFilter('all');
    setExpandedOrder(id);
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, { replace: true });
    window.setTimeout(() => {
      document.getElementById(`order-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  }, [searchParams, orders, setSearchParams]);

  // Employés de l'organisation (pour attribuer une vente / une commission).
  useEffect(() => {
    if (!commerceId) return;
    (async () => {
      try {
        const org = await getOrganizationByCommerce(commerceId);
        if (!org) return;
        setEmployees(await listOrganizationEmployees(org.id));
        // Statuts métier du socle (portée « commande »).
        try {
          const statuses = await listCoreStatuses(org.id);
          setCoreOrderStatuses(statuses.filter((s) => s.scope === 'order'));
        } catch { /* best-effort */ }
      } catch {
        /* best-effort */
      }
    })();
  }, [commerceId]);

  const empLabel = (e: CoreEmployee) => e.job_title || e.email || `Employé #${e.id}`;

  // Options de statut : statuts métier du socle si configurés, sinon statuts par défaut.
  const statusOptions = useMemo(
    () => (coreOrderStatuses.length > 0
      ? coreOrderStatuses.map((s) => ({ value: s.label, className: statusColorClass(s.color) }))
      : STATUS_KEYS.map((s) => ({ value: s, className: STATUS_CONFIG[s]?.className || STATUS_CONFIG['En attente'].className }))),
    [coreOrderStatuses],
  );
  const statusClassOf = (value: string | null) =>
    statusOptions.find((o) => o.value === value)?.className || 'bg-background-200/70 text-foreground-600';

  const handleOpenInExcel = async () => {
    if (!user || orders.length === 0) return;
    setCreatingExcel(true);
    setActionMsg(null);
    try {
      const headers = ['N°', 'Date', 'Client', 'Email', 'Téléphone', 'Ville', 'Articles', 'Quantité', 'Total', 'Devise', 'Statut', 'Paiement', 'Déjà payé', 'Restant'];
      const rows = orders.map((o) => [
        String(o.id),
        o.date_time ? new Date(o.date_time).toLocaleDateString('fr-FR') : '',
        o.nomclient || '',
        o.email || '',
        o.tel || '',
        o.ville || '',
        o.details || '',
        String(o.quantite ?? 1),
        String(toAmount(o.totalttc)),
        o.monaie || user?.monaie || 'MAD',
        o.statut_com_vendeur || '',
        o.methodepayment || '',
        String(toAmount(o.sommepayee)),
        String(toAmount(o.restant)),
      ]);
      await buildExcelDocument(user.id, `Commandes ${new Date().toISOString().slice(0, 10)}`, headers, rows, { sheetName: 'Commandes' });
      navigate('/dashboard/file-manager');
    } catch {
      setActionMsg("Impossible de créer le fichier Excel. Réessayez.");
    } finally {
      setCreatingExcel(false);
    }
  };

  const attributeOrder = async (order: CommandeRow, employeeId: number | null) => {
    setAttrBusy(order.id);
    try {
      const emp = employeeId ? employees.find((e) => e.id === employeeId) : null;
      const commission = emp ? computeCommission(emp.commission_type, emp.commission_value, toAmount(order.totalttc)) : null;
      await attributeOrderToEmployee(order.id, employeeId, commission);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, core_employee_id: employeeId, commission_amount: commission } : o)));
    } catch (err: unknown) {
      setActionMsg(err instanceof Error ? err.message : "Erreur lors de l'attribution de la vente.");
    } finally {
      setAttrBusy(null);
    }
  };

  const updateStatus = async (orderId: number, newStatus: string) => {
    const { error: updateError } = await supabase
      .from('commande')
      .update({ statut_com_vendeur: newStatus })
      .eq('id', orderId);
    if (!updateError) {
      fetchOrders();
      // Met à jour tout de suite le badge « Commandes » du menu latéral.
      window.dispatchEvent(new Event('orders-changed'));
      // Journalise le changement de statut — une annulation est tracée comme
      // une « suppression » pour une traçabilité complète côté marchand.
      const isCancel = /annul/i.test(newStatus);
      logOrderAction(isCancel ? 'delete' : 'update', orderId, 'order', { statut: newStatus });
    }
  };

  /** Produits digitaux présents dans une commande. */
  const digitalItemsOf = (order: CommandeRow) =>
    (Array.isArray(order.items_json) ? order.items_json : []).filter(
      (it) => (it.product_type || 'physical') === 'digital',
    );

  /**
   * Génère (une seule fois) la facture d'une commande payée.
   * Reprend le client de la commande, applique la TVA seulement si le
   * commerce est assujetti, puis enregistre le document dans Finance.
   */
  const autoInvoiceForOrder = async (order: CommandeRow, total: number, currency: string) => {
    if (!commerceId || total <= 0) return null;

    // Déjà facturée ? (référence unique CMD-<id>)
    const { data: existing } = await supabase
      .from('finance_invoices')
      .select('id, number')
      .eq('merchant_id', commerceId)
      .eq('reference', `CMD-${order.id}`)
      .maybeSingle();
    if (existing) return existing.number as string;

    const profile = company.profile;
    const { data: allInvoices } = await supabase
      .from('finance_invoices')
      .select('id, number, doc_type, source_quote_id')
      .eq('merchant_id', commerceId);

    const number = await reserveDocNumber({
      docType: 'invoice',
      merchantId: commerceId,
      profile,
      invoices: (allInvoices || []) as FinanceInvoice[],
      invoicePrefix: profile.invoice_prefix,
      reserveInvoiceNumber: company.reserveInvoiceNumber,
    });

    // TVA : uniquement si le commerce est assujetti (paramètres de facturation).
    const taxRate = profile.vat_subject ? Number(profile.default_vat_rate) || 0 : 0;
    const ht = taxRate > 0 ? total / (1 + taxRate / 100) : total;
    const subtotal = Math.round(ht * 100) / 100;
    const taxAmount = Math.round((total - subtotal) * 100) / 100;

    const description =
      order.details ||
      (Array.isArray(order.items_json) && order.items_json.length > 0
        ? order.items_json.map((it) => `${it.name} ×${it.quantity}`).join(', ')
        : `Commande #${order.id}`);

    const { data: inserted, error: insErr } = await supabase
      .from('finance_invoices')
      .insert({
        merchant_id: commerceId,
        number,
        doc_type: 'invoice',
        customer_name: order.nomclient || 'Client',
        customer_phone: order.tel || null,
        customer_email: order.email || null,
        customer_address: [order.adresse, order.ville].filter(Boolean).join(', ') || null,
        sale_date: order.date_time ? String(order.date_time).slice(0, 10) : null,
        reference: `CMD-${order.id}`,
        payment_terms: profile.payment_terms || 'Paiement à réception',
        payment_method: order.methodepayment || null,
        status: 'paid',
        issue_date: new Date().toISOString().slice(0, 10),
        due_date: new Date().toISOString().slice(0, 10),
        currency,
        subtotal,
        discount: 0,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        total,
        amount_paid: total,
        notes: `Facture générée automatiquement depuis la commande #${order.id}.`,
        created_by: commerceId,
      })
      .select('id, number')
      .single();
    if (insErr) throw insErr;

    await supabase.from('finance_invoice_items').insert({
      invoice_id: inserted.id,
      description,
      quantity: 1,
      unit_price: subtotal,
      item_type: 'custom',
      discount: 0,
      subtotal,
    });

    return inserted.number as string;
  };

  // Recherche debouncée de clients Zifek (accès express).
  useEffect(() => {
    const q = guestQuery.trim();
    if (q.length < 2) {
      setGuestResults([]);
      return;
    }
    let cancelled = false;
    setGuestSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await searchZifekClients(q, 8);
        if (!cancelled) setGuestResults(res);
      } catch {
        if (!cancelled) setGuestResults([]);
      } finally {
        if (!cancelled) setGuestSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [guestQuery]);

  const openAccessModal = (order: CommandeRow) => {
    setAccessOrder(order);
    setGuestQuery('');
    setGuestResults([]);
    setGrantMsg(null);
  };

  const closeAccessModal = () => {
    setAccessOrder(null);
    setGuestQuery('');
    setGuestResults([]);
    setGrantMsg(null);
  };

  const handleExpressGrant = async (client: ZifekClient) => {
    if (!accessOrder) return;
    const items = digitalItemsOf(accessOrder);
    if (items.length === 0) return;
    setGranting(true);
    setGrantMsg(null);
    try {
      for (const it of items) {
        await grantDigitalAccess({
          productId: Number(it.id),
          productName: it.name,
          userId: client.id,
          merchantId: commerceId,
          orderId: accessOrder.id,
          source: 'manual',
        });
      }
      setGrantMsg(`Accès accordé à ${client.name} pour ${items.length} produit(s) digital(aux).`);
      setGuestQuery('');
      setGuestResults([]);
    } catch (err: unknown) {
      setGrantMsg(err instanceof Error ? err.message : "Erreur lors de l'attribution de l'accès.");
    } finally {
      setGranting(false);
    }
  };
  const handleValidatePayment = async (order: CommandeRow) => {
    setValidatingId(order.id);
    setActionMsg(null);
    const cur = order.monaie || user?.monaie || 'MAD';
    try {
      const total = toAmount(order.totalttc);
      const { error: updError } = await supabase
        .from('commande')
        .update({ statut_com_vendeur: 'Livrée', sommepayee: String(total), restant: '0' })
        .eq('id', order.id);
      if (updError) throw updError;

      const digital = (Array.isArray(order.items_json) ? order.items_json : [])
        .filter((it) => (it.product_type || 'physical') === 'digital');
      const rawUser = String(order.user_id ?? '').trim();
      const buyerUid = /^\d+$/.test(rawUser) ? Number(rawUser) : null;

      let granted = 0;
      if (digital.length > 0 && buyerUid) {
        for (const it of digital) {
          await grantDigitalAccess({
            productId: Number(it.id),
            productName: it.name,
            userId: buyerUid,
            merchantId: commerceId,
            orderId: order.id,
            source: 'payment',
          });
          granted += 1;
        }
      }

      if (digital.length > 0 && !buyerUid) {
        setActionMsg(
          `Paiement validé. Commande passée par un invité : accordez l'accès digital manuellement depuis la page du produit (${digital.map((d) => d.name).join(', ')}).`,
        );
      } else if (granted > 0) {
        setActionMsg(`Paiement validé — accès à ${granted} produit(s) digital(aux) accordé(s) automatiquement.`);
      } else {
        setActionMsg('Paiement validé.');
      }

      // ── Facture générée automatiquement à la validation du paiement ──
      try {
        const invoiceNumber = await autoInvoiceForOrder(order, total, cur);
        if (invoiceNumber) {
          setActionMsg((prev) => `${prev} Facture ${invoiceNumber} générée dans Finance → Factures.`);
        }
      } catch (invErr: unknown) {
        setActionMsg(
          (prev) =>
            `${prev} La facture n'a pas pu être générée : ${invErr instanceof Error ? invErr.message : 'erreur inconnue'}.`,
        );
      }

      fetchOrders();
      window.dispatchEvent(new Event('orders-changed'));
      logOrderAction('payment', order.id, 'order', { statut: 'Livrée', total });
    } catch (err: unknown) {
      setActionMsg(err instanceof Error ? err.message : 'Erreur lors de la validation du paiement.');
    } finally {
      setValidatingId(null);
    }
  };

  /**
   * Ouvre la facture PDF (publiée et vérifiée) d'une commande payée.
   * Contrôle au passage que le document légal est complet (ICE + IF).
   */
  const openOrderInvoice = async (order: CommandeRow) => {
    if (!commerceId) return;
    setInvoiceBusy(order.id);
    setActionMsg(null);
    try {
      const { data: invoice, error: invErr } = await supabase
        .from('finance_invoices')
        .select('*')
        .eq('merchant_id', commerceId)
        .eq('reference', `CMD-${order.id}`)
        .maybeSingle();
      if (invErr) throw invErr;
      if (!invoice) {
        setActionMsg(
          'Aucune facture liée à cette commande : validez d’abord le paiement (la facture est alors générée automatiquement).',
        );
        return;
      }
      const { data: items } = await supabase
        .from('finance_invoice_items')
        .select('*')
        .eq('invoice_id', (invoice as { id: number }).id);

      const profile = company.profile;
      const missing = [
        !profile.company_name ? 'raison sociale' : '',
        !profile.ice ? 'ICE' : '',
        !profile.if_number ? 'IF' : '',
      ].filter(Boolean);

      printInvoice(
        invoice as Parameters<typeof printInvoice>[0],
        (items || []) as FinanceInvoiceItem[],
        profile,
      );
      setActionMsg(
        missing.length > 0
          ? `Facture PDF ouverte — attention : ${missing.join(', ')} manquant(s) dans Finance → Paramètres facturation (conformité Art. 145).`
          : `Facture ${(invoice as { number: string }).number} publiée en PDF — contrôles ICE / IF conformes.`,
      );
    } catch (err: unknown) {
      setActionMsg(err instanceof Error ? err.message : 'Impossible d’ouvrir la facture PDF.');
    } finally {
      setInvoiceBusy(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{t('orders.title', { defaultValue: 'Commandes' })}</h2>
          <p className="text-sm text-foreground-500 mt-1">{t('orders.subtitle', { defaultValue: 'Suivez et gérez toutes les commandes de votre boutique' })}</p>
        </div>
        <button
          onClick={handleOpenInExcel}
          disabled={creatingExcel || orders.length === 0}
          className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors whitespace-nowrap"
          title="Créer une feuille Excel modifiable dans l'application Documents"
        >
          {creatingExcel ? (
            <><i className="ri-loader-4-line animate-spin"></i>Création...</>
          ) : (
            <><i className="ri-file-excel-2-line"></i>Ouvrir dans Excel</>
          )}
        </button>
      </div>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[{ value: 'all', label: t('orders.filter_all', { defaultValue: 'Toutes' }) }, ...statusOptions.map((o) => ({ value: o.value, label: o.value }))].map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
              statusFilter === f.value
                ? 'bg-primary-50 text-primary-700 border border-primary-200'
                : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {actionMsg && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-accent-50 text-accent-700 text-sm flex items-start gap-2">
          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
          <span className="flex-1">{actionMsg}</span>
          <button onClick={() => setActionMsg(null)} className="cursor-pointer text-accent-700 hover:text-accent-900">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-foreground-600 mb-3">{error}</p>
          <button onClick={fetchOrders} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors">
            {t('common.retry', { defaultValue: 'Réessayer' })}
          </button>
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-800 mb-1">{t('orders.empty', { defaultValue: 'Aucune commande' })}</h3>
          <p className="text-sm text-foreground-500">{t('orders.empty_hint', { defaultValue: 'Les commandes apparaîtront ici quand vous en recevrez' })}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const statusCfg = { label: order.statut_com_vendeur || 'En attente', className: statusClassOf(order.statut_com_vendeur) };
            const isExpanded = expandedOrder === order.id;
            const clientName = order.nomclient || 'Client';
            const total = toAmount(order.totalttc);
            const cur = order.monaie || user?.monaie || 'MAD';

            return (
              <div key={order.id} id={`order-${order.id}`} className={`bg-background-50 border rounded-lg overflow-hidden transition-colors ${expandedOrder === order.id ? 'border-primary-300' : 'border-background-200/70'}`}>
                <div
                  className="flex flex-wrap items-center gap-3 px-5 py-4 cursor-pointer hover:bg-background-50/80 transition-colors"
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="min-w-[80px]">
                    <span className="text-xs text-foreground-400">#{order.id}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground-900 truncate">{clientName}</div>
                    <div className="text-xs text-foreground-500">
                      {order.date_time
                        ? new Date(order.date_time).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-foreground-900 whitespace-nowrap">
                      {total.toLocaleString()} {cur}
                    </div>
                    {equivalence(total, cur) && (
                      <div className="text-[10px] text-foreground-400 whitespace-nowrap">{equivalence(total, cur)}</div>
                    )}
                  </div>
                  {order.typecommande === 'digital' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-primary-50 text-primary-700">
                      <i className="ri-download-cloud-2-line"></i>Digital
                    </span>
                  )}
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusCfg.className}`}>
                    {statusCfg.label}
                  </span>
                  <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
                </div>

                {isExpanded && (
                  <div className="px-5 py-4 border-t border-background-200/70 bg-background-50/50">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <button
                        onClick={() => handleValidatePayment(order)}
                        disabled={validatingId === order.id}
                        className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-accent-500 text-background-50 hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {validatingId === order.id ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>Validation...
                          </>
                        ) : (
                          <>
                            <i className="ri-check-double-line"></i>Valider le paiement
                          </>
                        )}
                      </button>
                      {digitalItemsOf(order).length > 0 && (
                        <button
                          onClick={() => openAccessModal(order)}
                          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-key-2-line"></i>Donner accès à un client
                        </button>
                      )}
                      {toAmount(order.sommepayee) > 0 && (
                        <>
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-accent-50 text-accent-700">
                            <i className="ri-checkbox-circle-line"></i>Payé
                          </span>
                          <button
                            onClick={() => openOrderInvoice(order)}
                            disabled={invoiceBusy === order.id}
                            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                            title="Ouvrir la facture PDF de cette commande"
                          >
                            {invoiceBusy === order.id ? (
                              <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                              <i className="ri-file-pdf-2-line"></i>
                            )}
                            Facture PDF
                          </button>
                        </>
                      )}
                      {order.typecommande === 'digital' && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                          <i className="ri-download-cloud-2-line"></i>Commande digitale
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                      {order.email && (
                        <div>
                          <span className="text-xs text-foreground-400 block">{t('orders.email', { defaultValue: 'Email' })}</span>
                          <span className="text-foreground-900 break-all">{order.email}</span>
                        </div>
                      )}
                      {order.tel && (
                        <div>
                          <span className="text-xs text-foreground-400 block">{t('orders.phone', { defaultValue: 'Téléphone' })}</span>
                          <span className="text-foreground-900">{order.tel}</span>
                        </div>
                      )}
                      {(order.ville || order.adresse) && (
                        <div>
                          <span className="text-xs text-foreground-400 block">Livraison</span>
                          <span className="text-foreground-900">{[order.adresse, order.ville].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      <div>
                        <span className="text-xs text-foreground-400 block">{t('orders.payment', { defaultValue: 'Paiement' })}</span>
                        <span className="text-foreground-900 capitalize">{order.methodepayment || 'À convenir'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-400 block">{t('orders.status', { defaultValue: 'Statut' })}</span>
                        <select
                          value={order.statut_com_vendeur || 'En attente'}
                          onChange={(e) => updateStatus(order.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs bg-background-50 border border-background-200/70 rounded px-2 py-1 text-foreground-900 cursor-pointer focus:outline-none"
                        >
                          {statusOptions.map((s) => (
                            <option key={s.value} value={s.value}>{s.value}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Attribution de la vente / commission par employé */}
                    <div className="mb-4 rounded-lg border border-background-200/70 bg-background-100/50 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground-700">
                          <i className="ri-user-star-line text-secondary-600"></i>
                          Vente attribuée à
                        </span>
                        <select
                          value={order.core_employee_id ?? ''}
                          onChange={(ev) => attributeOrder(order, ev.target.value ? Number(ev.target.value) : null)}
                          onClick={(ev) => ev.stopPropagation()}
                          disabled={attrBusy === order.id || employees.length === 0}
                          className="text-xs bg-background-50 border border-background-200/70 rounded px-2 py-1 text-foreground-900 cursor-pointer focus:outline-none disabled:opacity-60"
                        >
                          <option value="">{employees.length === 0 ? 'Aucun employé enregistré' : 'Non attribuée'}</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>{empLabel(emp)}</option>
                          ))}
                        </select>
                      </div>
                      {order.core_employee_id != null && (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <i className="ri-hand-coin-line text-foreground-400"></i>
                          <span className="text-foreground-500">Commission :</span>
                          <span className="font-semibold text-primary-700">
                            {toAmount(order.commission_amount).toLocaleString()} {cur}
                          </span>
                        </div>
                      )}
                    </div>

                    {order.details && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">{t('orders.items', { defaultValue: 'Articles' })}</h4>
                        <p className="text-sm text-foreground-800">{order.details}</p>
                      </div>
                    )}

                    <PackOrderDetails orderId={order.id} />

                    {order.noteclient && (
                      <div className="mb-4 px-3 py-2 bg-background-100 rounded-lg">
                        <span className="text-xs text-foreground-400 block mb-0.5">Note du client</span>
                        <p className="text-sm text-foreground-700">{order.noteclient}</p>
                      </div>
                    )}

                    {/* Timeline & Notes du socle central (spec 31 / 32) */}
                    <div className="mb-4">
                      <EntityActivityTabs
                        commerceId={commerceId}
                        entityType="order"
                        entityId={order.id}
                        actorName={user?.name}
                        actorUserId={user?.id}
                      />
                    </div>

                    <div className="pt-3 border-t border-background-200/70 space-y-1 text-sm">
                      <div className="flex justify-between text-foreground-500">
                        <span>{t('orders.items', { defaultValue: 'Articles' })}</span>
                        <span>{order.quantite ?? 1}</span>
                      </div>
                      <div className="flex justify-between font-bold text-foreground-950 pt-1 border-t border-background-200/70">
                        <span>{t('orders.total', { defaultValue: 'Total' })}</span>
                        <span>{total.toLocaleString()} {cur}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {orders.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-sm text-foreground-500">
          <span>{orders.length} commande{orders.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {accessOrder && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeAccessModal}></div>
          <div className="relative bg-background-50 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] flex flex-col mx-0 sm:mx-4">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
              <div>
                <h3 className="text-sm font-bold text-foreground-950">Donner accès à un client</h3>
                <p className="text-xs text-foreground-500">Commande #{accessOrder.id}</p>
              </div>
              <button onClick={closeAccessModal} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              <div className="mb-4">
                <p className="text-xs font-semibold text-foreground-600 mb-2">Produits digitaux de la commande</p>
                <div className="flex flex-wrap gap-2">
                  {digitalItemsOf(accessOrder).map((it) => (
                    <span key={it.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                      <i className="ri-download-cloud-2-line"></i>{it.name}
                    </span>
                  ))}
                </div>
              </div>
              {grantMsg && (
                <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-accent-50 text-accent-700">{grantMsg}</div>
              )}
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={guestQuery}
                  onChange={(e) => setGuestQuery(e.target.value)}
                  placeholder="Rechercher un client (nom, pseudo ou email)"
                  className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
                {guestQuery.trim().length >= 2 && (
                  <div className="mt-1 bg-background-50 border border-background-200/70 rounded-lg max-h-64 overflow-y-auto py-1">
                    {guestSearching ? (
                      <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-500">
                        <i className="ri-loader-4-line animate-spin"></i>Recherche...
                      </div>
                    ) : guestResults.length === 0 ? (
                      <div className="px-3 py-2.5 text-xs text-foreground-400">Aucun client trouvé.</div>
                    ) : (
                      guestResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleExpressGrant(c)}
                          disabled={granting}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                        >
                          <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {c.image ? (
                              <img src={c.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <i className="ri-user-line text-foreground-400 text-sm"></i>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground-900 truncate">{c.name}</p>
                            <p className="text-xs text-foreground-400 truncate">{c.email || `@${c.userName}`}</p>
                          </div>
                          <span className="text-xs font-semibold text-primary-600 whitespace-nowrap">Donner accès</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <p className="mt-3 text-[11px] text-foreground-400">
                Idéal pour une commande invitée : choisissez le client Zifek à qui ouvrir l&apos;accès aux fichiers numériques de cette commande.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}